<template>
  <v-expansion-panel :id="`${testName}`">
    <v-expansion-panel-header>
      <h2 :id="testId">{{ testName }}</h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="medium-test-grid"
      >
        <div v-if="testId === 'PHOX2B'">
          <p>
            <a target="_blank" href="https://www.vcgs.org.au/tests/phox2b/"
              >This test
            </a>
            looks for the poly-A expansion repeat in PHOX2B which is known to
            cause Congenital Central Hypoventilation Syndrome (CCHS). This rare
            disorder is characterised by abnormal control over breathing,
            especially during sleep, resulting in hypoventilation.
          </p>
          <v-alert
            text
            outlined
            type="warning"
            icon="mdi-cash-usd-outline"
            class="mr-1 pl-0 pb-0"
          >
            <p>Patient to be referred to a genetics clinic for testing.</p>
          </v-alert>
        </div>
        <div v-if="testId === 'deafness_mlpa'">
          <p>
            This
            <a
              target="_blank"
              href="https://www.vcgs.org.au/tests/deafness-mlpa/"
              >MLPA assay
            </a>
            for analysis of the OTOA, STRC, CATSPER2 genes. These genes account
            for the majority of sensorineural hearing loss (SNHL) associated
            copy number changes. This test is suitable as a second tier test
            following a negative result from GJB2/GJB6 (Connexin 26/30) testing.
          </p>
        </div>
        <div>
          <v-row v-if="testId === 'deafness_mlpa'">
            <v-col class="custom-col">
              <span class="text-title">Carrier test?*</span>
              <v-select
                v-model="carrier"
                :items="carrierYesNo"
                :rules="ruleValidBoolean"
                item-text="key"
                item-value="value"
                :id="testId + '.carrier'"
                outlined
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>
          <v-row v-else-if="testId !== 'PHOX2B'">
            <v-col class="custom-col">
              <span class="text-title">Predictive test?*</span>
              <v-select
                v-model="predictive"
                :items="predictiveYesNo"
                :rules="ruleValidBoolean"
                item-text="key"
                item-value="value"
                :id="testId + '.predictive'"
                outlined
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>
        </div>
        <v-alert
          v-if="predictive || predictiveAndCarrier"
          text
          type="warning"
          outlined
          icon="mdi-cash-usd-outline"
        >
          Predictive genetic testing requires the patient to collect two
          separate samples. Please ensure the patient is aware of this.
        </v-alert>
        <v-row v-if="predictive || carrier || predictiveAndCarrier">
          <v-col class="custom-col">
            <span class="text-title"
              >Family member previously tested at VCGS*</span
            >
            <v-select
              v-model="previouslyTested"
              :items="yesNoOptions"
              :rules="ruleValidBoolean"
              item-text="key"
              item-value="value"
              :id="testId + '.familyPrevTested'"
              outlined
              hide-details="auto"
            ></v-select>
          </v-col>
        </v-row>
        <v-row
          v-if="
            (predictive || carrier || predictiveAndCarrier) &&
              previouslyTested === true
          "
        >
          <v-col class="custom-col">
            <span class="text-title">VCGS sample ID</span>
            <v-text-field
              v-model="posCtrlSampleId"
              :id="testId + '.posCtrlSampleId'"
              outlined
              hide-details="auto"
            ></v-text-field>
            <span class="text-hint"
              >e.g. 20C100123. This is located in the top right section of our
              reports.</span
            >
          </v-col>
        </v-row>
        <v-row
          v-if="
            (predictive || carrier || predictiveAndCarrier) && previouslyTested
          "
        >
          <v-col class="custom-col">
            <span class="text-title">Family member name*</span>
            <v-text-field
              v-model="posCtrlName"
              :id="testId + '.posCtrlName'"
              :rules="ruleNotNull"
              outlined
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row
          v-if="
            (predictive || carrier || predictiveAndCarrier) && previouslyTested
          "
        >
          <v-col class="custom-col">
            <span class="text-title">Family member date of birth</span>
            <v-menu
              v-model="familyMemberDOBMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              :rules="ruleNotNull"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="familyMemberDOBDisplay"
                  :id="testId + '.posCtrlBirthDate'"
                  style="grid-area: posCtrlBirthDate"
                  v-on="on"
                  :rules="familyMemberDOBDisplay != '' ? ruleDate : []"
                  outlined
                  hide-details="auto"
                ></v-text-field>
              </template>
              <v-date-picker
                locale="en-au"
                :min="minDate"
                :max="maxDate"
                v-model="familyDOBVal"
                @input="familyMemberDOBMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <div
          class="mb-3 mt-3 mb-md-6"
          v-if="
            this.$dataset.isUploadPreviousReportEnabled &&
              previouslyTested === false &&
              (carrier || predictive || predictiveAndCarrier)
          "
        >
          <UploadComponent
            :testId="testId"
            :testName="testName"
            :updateOnDestroy="true"
            @updateUploadObj="updateUploadObj"
            uploadType="previousReport"
          />
        </div>
        <v-row v-if="predictive || carrier || predictiveAndCarrier">
          <v-col class="custom-col">
            <span class="text-title"
              >Relationship of this patient to positive control*</span
            >
            <v-text-field
              v-model="rhipToPosCtrl"
              :id="testId + '.rhipToPosCtrl'"
              :rules="ruleNotNull"
              outlined
              hide-details="auto"
            ></v-text-field>
            <span class="text-hint">e.g. sibling, parent, aunt, etc.</span>
          </v-col>
        </v-row>
        <v-row v-if="predictive || carrier || predictiveAndCarrier">
          <v-col class="custom-col">
            <span class="text-title"
              >Is the person being tested affected by the same condition?*</span
            >
            <v-text-field
              v-model="relationAffected"
              :id="testId + '.relationAffected'"
              :rules="ruleNotNull"
              outlined
              hide-details="auto"
            ></v-text-field>
            <span class="text-hint">e.g. mildly affected</span>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title">Clinical notes*</span>
            <v-textarea
              v-model="clinicalNotes"
              :id="testId + '.clinicalNotes'"
              :rules="ruleNotNull"
              outlined
              hide-details="auto"
            ></v-textarea>
            <span class="text-hint"
              >e.g. clinical indication for testing, relevant family history,
              results of relevant prior genetic and non-genetic investigations,
              biochemistry, histopathology and imaging, etc.</span
            >
          </v-col>
        </v-row>
        <TestPriceAlert :price="price" :medicare="medicare" />
      </v-form>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FormRulesMixin from "../mixins/FormRulesMixin.vue";
import DropDownMixin from "../mixins/DropDownMixin.vue";
import { useSectionStore } from "../store/SectionStore";
import TestPriceAlert from "./common/TestPriceAlert.vue";
import UploadMixin from "../mixins/UploadMixin.vue";
import UploadComponent from "../components/upload/UploadComponent.vue";
import RetrieveJSONMixin from "../mixins/RetrieveJSONMixin.vue";
import { EventBus } from "@/event-bus";

export default {
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
  },
  mixins: [FormRulesMixin, DropDownMixin, UploadMixin, RetrieveJSONMixin],
  components: { TestPriceAlert, UploadComponent },

  name: "",
  data: () => ({
    urgentKit: false,
    urgentReason: "",
    mediumExomePricing: null,
    previouslyTested: null,
    relationAffected: "",
    rhipToPosCtrl: "",
    carrier: null,
    predictive: null,
    predictiveAndCarrier: null,
    carrierYesNo: [
      { key: "Yes", value: true },
      { key: "No - diagnostic test", value: false },
    ],
    predictiveYesNo: [
      { key: "Yes", value: true },
      { key: "No - diagnostic test", value: false },
    ],
    predictiveCarrierYesNo: [
      { key: "Yes", value: true },
      { key: "No - diagnostic test", value: false },
    ],
    yesNoOptions: [
      { key: "Yes", value: true },
      { key: "No", value: false },
    ],
    posCtrlSampleId: "",
    clinicalNotes: "",
    posCtrlName: "",
    familyMemberDOBMenu: false,
    familyDOBVal: null,
    familyMemberDOBDisplay: "",
    minDate: "1801-01-01",
    maxDate: new Date().toJSON().slice(0, 10),
  }),
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.mediumExomePricing = await this.execRetrieveJson(
      "json/pricing/medium_pricing.json"
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    const element = document.getElementById(this.testName + "-title");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  },
  watch: {
    price: function() {
      this.$emit("toggleTotalPrice", this.price, this.testId);
    },
    familyDOBVal: function() {
      this.familyMemberDOBDisplay = this.familyDOBVal
        ? this.formatDate(this.familyDOBVal)
        : "";
    },
  },
  computed: {
    getPriceDetails() {
      if (!this.mediumExomePricing) return null;
      return this.mediumExomePricing[this.testId];
    },
    price() {
      const priceDetails = this.getPriceDetails;
      if (priceDetails) {
        if (useSectionStore().victorianPatient) {
          if (priceDetails.victorianPrice === 0.0) {
            useSectionStore().freeTestForVictorian = true;
          }
          return priceDetails.victorianPrice;
        }
        return priceDetails.price;
      }
      return null;
    },
    testCode() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.testCode;
      } else {
        return "";
      }
    },
    medicare() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.medicare;
      } else {
        return null;
      }
    },
  },
  methods: {
    formatDate: function(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },
    showFamilyDetails() {
      if (this.predictive || this.carrier || this.predictiveAndCarrier) {
        return true;
      } else return false;
    },
    hasNotes(testId) {
      if (typeof this.getTestNotes(testId) !== "undefined") {
        return true;
      } else return false;
    },
    getTestNotes(testId) {
      return this.supportedTests.find((test) => test.value === testId).notes;
    },
    reverseFormatDate: function(date) {
      if (!date) return null;
      const [day, month, year] = date.split("/");
      return `${year}-${month}-${day}`;
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    transferData: function() {
      return {
        testType: this.testId,
        urgent: this.urgentKit,
        urgentReason: this.urgentReason,
        carrier: this.predictiveAndCarrier ? true : this.carrier,
        predictiveTest: this.predictiveAndCarrier ? true : this.predictive,
        familyPrevTested: this.showFamilyDetails()
          ? this.previouslyTested
          : null,
        posCtrlSampleId: this.showFamilyDetails() ? this.posCtrlSampleId : "",
        relationAffected: this.showFamilyDetails() ? this.relationAffected : "",
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        medicare: this.medicare,
        rhipToPosCtrl: this.showFamilyDetails() ? this.rhipToPosCtrl : "",
        posCtrlName: this.showFamilyDetails() ? this.posCtrlName : "",
        posCtrlBirthDate: this.showFamilyDetails()
          ? this.reverseFormatDate(this.familyMemberDOBDisplay)
          : "",
        clinicalNotes: this.clinicalNotes,
      };
    },
  },
};
</script>

<style scoped></style>
